// ============================================
// Path: /frontend/src/components/auth/RegisterForm.jsx
// Purpose: Registration form component with validation
// CSS FIXES: 7 specific changes applied
// ============================================

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { User, Mail, KeyRound, CheckCircle, Circle, Phone, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../../hooks/useToast';

export function RegisterForm() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('USER');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const validatePhone = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    
    if (!digitsOnly) {
      setPhoneError('Phone number is required');
      return false;
    }

    if (digitsOnly.startsWith('254')) {
      if (digitsOnly.length !== 12) {
        setPhoneError('Kenyan number must be 12 digits (254XXXXXXXXX)');
        return false;
      }
      
      const kenyaMobileRegex = /^254(7\d{8}|1\d{8})$/;
      if (!kenyaMobileRegex.test(digitsOnly)) {
        setPhoneError('Invalid Kenyan number. Must be 2547XXXXXXXX or 2541XXXXXXXX');
        return false;
      }
    } else {
      if (digitsOnly.length < 10 || digitsOnly.length > 15) {
        setPhoneError('International number must be 10-15 digits');
        return false;
      }
    }

    setPhoneError('');
    return true;
  };

  const sanitizeName = (value) => {
    return value
      .replace(/[<>{}[\]\\\/]/g, '') // Remove dangerous chars
      .replace(/\s+/g, ' ')           // Replace multiple spaces with single space
      .slice(0, 100);                 // Max 100 characters
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    
    if (value.startsWith('+')) {
      value = value.substring(1);
    }
    
    const digitsOnly = value.replace(/\D/g, '');
    setPhone(digitsOnly);
    
    if (digitsOnly) {
      validatePhone(digitsOnly);
    } else {
      setPhoneError('');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Honeypot check - bots will fill this field
    if (honeypot) {
      console.log('Bot detected via honeypot');
      return; // Silently reject without showing error
    } 
    
    if (!validatePhone(phone)) {
      toast({
        variant: 'destructive',
        title: 'Invalid Phone Number',
        description: phoneError,
      });
      return;
    }

// Validate password strength
    if (password.length < 8) {
      toast({
        variant: 'destructive',
        title: 'Weak Password',
        description: 'Password must be at least 8 characters long.',
      });
      return;
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      toast({
        variant: 'destructive',
        title: 'Weak Password',
        description: 'Password must contain uppercase, lowercase, and number.',
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Passwords do not match.',
      });
      return;
    }

    setLoading(true);

    try {
      await register({ fullName, email, phone, password, role });
      toast({
        title: 'Registration Successful!',
        description: 'Your account is pending admin approval.',
      });
      setTimeout(() => {
        navigate('/');
      }, 9000);

       } catch (error) {
          // Extract validation errors from backend
          const errors = error.response?.data?.errors;
          
          if (errors && Array.isArray(errors) && errors.length > 0) {
            // Show the first validation error (the most relevant one)
            toast({
              variant: 'destructive',
              title: 'Validation Error',
              description: errors[0].msg,
            });
          } else {
            // Fallback to general error message
            toast({
              variant: 'destructive',
              title: 'Registration Error',
              description: error.response?.data?.message || 'An unexpected error occurred.',
            });
          }
        } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="w-full max-w-md mx-4">
      {/* FIX 1: CardHeader padding - p-4 sm:p-6 → p-3 sm:p-4 md:p-6 */}
      {/* FIX 2: CardTitle size - text-xl → text-lg sm:text-xl md:text-2xl lg:text-3xl */}
      <CardHeader className="text-center space-y-1 sm:space-y-2 p-3 sm:p-4 md:p-6">
        <CardTitle className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">Create an Account</CardTitle>
        <CardDescription className="text-xs sm:text-sm md:text-base">
          Join our community to start listing and browsing
        </CardDescription>
      </CardHeader>
      <CardContent className="p-3 sm:p-4 md:p-6">
        <form onSubmit={handleRegister} className="space-y-3 sm:space-y-4">
          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="name" className="text-xs sm:text-sm">Full Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
              <Input
                id="name"
                placeholder="John Doe"
                required
                className="pl-9 sm:pl-10 h-9 sm:h-10 text-xs sm:text-sm"
                value={fullName}
                onChange={(e) => setFullName(sanitizeName(e.target.value))}
              />
            </div>
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="email" className="text-xs sm:text-sm">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                required
                className="pl-9 sm:pl-10 h-9 sm:h-10 text-xs sm:text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            {/* FIX 6: Phone label inline span - added text-[10px] sm:text-xs */}
            <Label htmlFor="phone" className="text-xs sm:text-sm">
              Phone Number <span className="text-muted-foreground text-[10px] sm:text-xs">(e.g., 254721234567)</span>
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
              <Input
                id="phone"
                type="tel"
                placeholder="254721234567 (no + sign)"
                required
                maxLength={15}
                className={`pl-9 sm:pl-10 h-9 sm:h-10 text-xs sm:text-sm ${
                  phoneError ? 'border-red-500 focus:ring-red-500' : ''
                }`}
                value={phone}
                onChange={handlePhoneChange}
              />
            </div>
            {phoneError && (
              <p className="text-[10px] sm:text-xs text-red-500 mt-1">{phoneError}</p>
            )}
            <p className="text-[10px] sm:text-xs text-muted-foreground">
              Kenyan: 254XXXXXXXXX (no + sign) • International: 10-15 digits
            </p>
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="password" className="text-xs sm:text-sm">Password</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}"
                title="Password must be at least 8 characters and contain uppercase, lowercase, and number"
                className="pl-9 sm:pl-10 pr-10 h-9 sm:h-10 text-xs sm:text-sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="confirm-password" className="text-xs sm:text-sm">Confirm Password</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
              <Input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                required
                className="pl-9 sm:pl-10 pr-10 h-9 sm:h-10 text-xs sm:text-sm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Honeypot field - hidden from humans, visible to bots */}
          <div className="hidden" aria-hidden="true">
            <Label htmlFor="website">Website (leave blank)</Label>
            <Input
              id="website"
              name="website"
              type="text"
              tabIndex="-1"
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          {/* Role Selection */}
          <div className="space-y-2 sm:space-y-3">
            <Label className="text-xs sm:text-sm">Register as: <span className="text-red-500">*</span></Label>
            {/* FIX 7: Role selection gap - gap-2 sm:gap-3 → gap-2 sm:gap-3 md:gap-4 */}
            <div className="grid grid-cols-1 gap-2 sm:gap-3 md:gap-4">
              {/* User Option */}
              {/* FIX 3: Button padding - p-2.5 sm:p-3 → p-2 sm:p-3 md:p-4 */}
              <button
                type="button"
                onClick={() => setRole('USER')}
                className={`flex items-center justify-between p-2 sm:p-3 md:p-4 border-2 rounded-lg transition-all duration-200 ${
                  role === 'USER'
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center">
                  <div className="mr-2 sm:mr-3">
                    {role === 'USER' ? (
                      <CheckCircle className="text-primary w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <Circle className="text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-xs sm:text-sm md:text-base">User</div>
                    {/* FIX 4: Description text - text-[10px] sm:text-xs md:text-sm → text-xs sm:text-sm */}
                    <div className="text-xs sm:text-sm text-muted-foreground">Browse and purchase listings</div>
                  </div>
                </div>
              </button>

              {/* Dealer Option */}
              <button
                type="button"
                onClick={() => setRole('DEALER')}
                className={`flex items-center justify-between p-2 sm:p-3 md:p-4 border-2 rounded-lg transition-all duration-200 ${
                  role === 'DEALER'
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center">
                  <div className="mr-2 sm:mr-3">
                    {role === 'DEALER' ? (
                      <CheckCircle className="text-primary w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <Circle className="text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-xs sm:text-sm md:text-base">Dealer</div>
                    <div className="text-xs sm:text-sm text-muted-foreground">Sell vehicles and manage inventory</div>
                  </div>
                </div>
              </button>

              {/* Real Estate Agent Option */}
              <button
                type="button"
                onClick={() => setRole('REAL_ESTATE_AGENT')}
                className={`flex items-center justify-between p-2 sm:p-3 md:p-4 border-2 rounded-lg transition-all duration-200 ${
                  role === 'REAL_ESTATE_AGENT'
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center">
                  <div className="mr-2 sm:mr-3">
                    {role === 'REAL_ESTATE_AGENT' ? (
                      <CheckCircle className="text-primary w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <Circle className="text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-xs sm:text-sm md:text-base">Real Estate Agent</div>
                    <div className="text-xs sm:text-sm text-muted-foreground">List and manage properties</div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* FIX 5: Submit button margin - !mt-5 sm:!mt-6 → mt-4 sm:mt-6 (removed !important) */}
          <Button 
            type="submit" 
            className="w-full mt-4 sm:mt-6 font-semibold text-xs sm:text-sm h-10 sm:h-11" 
            disabled={loading || !!phoneError}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
        <div className="mt-3 sm:mt-4 text-center text-[10px] sm:text-xs md:text-sm">
          Already have an account?{' '}
          <Link 
            to="/login" 
            className="font-medium text-primary hover:underline transition-colors"
          >
            Login
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}